/* eslint-disable factorial/no-raw-html */
import React, { memo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { HighlightBanner, WidgetEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import { CommunityPostListDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useCanCreatePosts } from 'modules/dashboard/components/Groups/GroupPicker/GroupInfo'
import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import { COMMUNITY_ITEM_TYPES, COMMUNITY_WIDGET_TYPES } from 'modules/dashboard/lib/events'
import CommunityPost from 'modules/engagement/components/CommunityPost'
import { useShowKudosCard } from 'modules/posts/hooks/useShowKudosCard'

import Skeleton from './Skeleton'
import { getQueryFiltersFromDropdown } from './helpers'
import * as styles from './index.vanilla.css'

type CommunityPostListProps = {
  selectedCommunity: CommunitiesDropdown
}

const CommunityPostList = ({ selectedCommunity }: CommunityPostListProps) => {
  const {
    data,
    loading: loadingPosts,
    controls: { fetchMore: fetchMorePosts, hasMore: hasMorePosts, loading: loadingNextPosts },
  } = usePaginatedQuery(CommunityPostListDocument, {
    variables: getQueryFiltersFromDropdown(selectedCommunity),
    extractConnection: (dataToExtract) =>
      dataToExtract.posts.postsConnection ?? {
        pageInfo: {
          hasNextPage: false,
        },
        edges: [],
      },
  })

  const showKudosCard = useShowKudosCard({
    communitiesDropdown: selectedCommunity,
  })

  const canCreatePosts = useCanCreatePosts({ communitiesDropdown: selectedCommunity })

  const handleKudosClick = () => {
    track(trackingEvents.dashboard.clickOnKudosShareLove)
    navigate(navigation.dashboard.kudos.new.fullPath())
  }

  const kudosBannerProps = {
    title: i18n.t('posts.kudos.new_banner.title'),
    subtitle: i18n.t('posts.kudos.new_banner.subtitle'),
    buttonLabel: i18n.t('posts.kudos.new_banner.action'),
    onClick: handleKudosClick,
  }

  const getNextPage = () => {
    if (hasMorePosts) {
      fetchMorePosts()
    }
  }

  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const location = useLocation()

  const handlePostClick = (postId: string) => {
    const postIndex = data?.posts.postsConnection.edges.findIndex(
      (edge) => `${edge.node.id}` === postId
    )

    if (postIndex) {
      const order = postIndex + 1
      track(trackingEvents.dashboard.clickOnCommunityItem, {
        widget: COMMUNITY_WIDGET_TYPES.POST,
        type: COMMUNITY_ITEM_TYPES.REGULAR_POST,
        order,
      })
    }

    const isFromCommunities = location.pathname.includes(navigation.communities.fullPath())
    if (isFromCommunities) {
      navigate(
        navigation.communities.post.id.fullPath({
          id: postId,
        })
      )
    } else {
      navigate(
        navigation.dashboard.post.id.fullPath({
          id: postId,
        })
      )
    }
  }

  const handleCommentClick = (postId: string) => {
    handlePostClick(postId)
  }

  const handleClickCreatePost = () => {
    track(trackingEvents.dashboard.clickOnCreateNewPost)
    navigate(navigation.dashboard.post.new.fullPath())
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  const handleGroupClick = (groupId: string) => {
    // TODO
  }

  const callbacks = {
    onPostClick: handlePostClick,
    onCommentClick: handleCommentClick,
    onGroupClick: handleGroupClick,
  }

  const posts = data?.posts.postsConnection.edges?.map((edge) => edge.node.id) || []

  const firstPostId = posts?.[0]

  return (
    <Box flexDirection="column" gap="s16">
      {firstPostId ? (
        <Box className={styles.communityPostWrapper}>
          <CommunityPost id={firstPostId} {...callbacks} />
        </Box>
      ) : (
        <>{loadingPosts && <Skeleton />}</>
      )}
      {showKudosCard && <HighlightBanner {...kudosBannerProps} />}
      {posts.slice(1).map((postId, index) => (
        <Box key={postId} className={styles.communityPostWrapper}>
          <CommunityPost
            id={postId}
            {...callbacks}
            onVisible={index + 2 === posts.length ? getNextPage : undefined}
          />
        </Box>
      ))}
      {!posts.length && !loadingPosts && (
        <Box className={styles.emptyStateWrapper}>
          <WidgetEmptyState
            title={i18n.t('new_dashboard.posts.empty.title')}
            description={i18n.t('new_dashboard.posts.empty.description')}
            emoji="📰"
            actions={
              canCreatePosts
                ? [
                    {
                      label: i18n.t('new_dashboard.posts.write_post'),
                      icon: Pencil,
                      onClick: handleClickCreatePost,
                      variant: 'outline',
                    },
                  ]
                : undefined
            }
          />
        </Box>
      )}
      {loadingNextPosts && <Skeleton />}
    </Box>
  )
}

export default memo(CommunityPostList)
